import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Lightbulb, Check, X, Loader2, ArrowRight, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { Celebrate, Mascot } from "@/components/Mascot";
import { useAiJob, isStopped } from "@/lib/aiJob";
import { Input } from "@/components/ui/input";
import { Markdown } from "@/components/ChatMessage";
import { FlashcardsPanel } from "@/components/FlashcardsPanel";
import { PracticeTabs } from "@/components/PracticeTabs";
import { Locked } from "@/components/Paywall";

function Question({ q, i, setId, onAnswered }) {
  const [ans, setAns] = useState("");
  const [hint, setHint] = useState(false);
  const [busy, setBusy] = useState(false);
  const check = async () => {
    if (!ans.trim()) return;
    setBusy(true);
    try {
      const { data } = await http.post(`/practice/${setId}/answer`, { index: i, answer: ans });
      onAnswered(i, data);
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const a = q.attempt;
  return (
    <div className="rounded-2xl border border-white/10 bg-[#121725] p-6 rise" style={{ animationDelay: `${i * 60}ms` }} data-testid={`practice-question-${i}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="h-6 w-6 rounded-full bg-sky-400/15 text-sky-300 text-xs flex items-center justify-center font-medium">{i + 1}</span>
        {a && (
          <span data-testid={`practice-result-${i}`} className={`text-xs px-2 py-0.5 rounded-full border ${a.correct ? "text-emerald-300 border-emerald-300/30 bg-emerald-500/10" : "text-red-300 border-red-300/30 bg-red-500/10"}`}>
            {a.correct ? "Correct" : "Not quite"}
          </span>
        )}
      </div>
      <Markdown>{q.question}</Markdown>
      {!a ? (
        <div className="mt-4 space-y-3">
          {hint && <p className="text-sm text-yellow-200/90 flex gap-2"><Lightbulb className="h-4 w-4 shrink-0 mt-0.5" />{q.hint}</p>}
          <div className="flex gap-2">
            <Input value={ans} onChange={(e) => setAns(e.target.value)} onKeyDown={(e) => e.key === "Enter" && check()}
              placeholder="Your answer…" className="bg-white/5 border-white/10" data-testid={`practice-answer-input-${i}`} />
            <button onClick={check} disabled={busy} data-testid={`practice-check-btn-${i}`}
              className="px-4 rounded-lg bg-sky-400 text-white text-sm font-medium hover:bg-sky-500 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check"}
            </button>
          </div>
          {!hint && q.hint && <button onClick={() => setHint(true)} className="text-xs text-neutral-500 hover:text-yellow-200" data-testid={`practice-hint-btn-${i}`}>Show hint</button>}
        </div>
      ) : (
        <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
          <p className="text-sm text-neutral-400">Your answer: <span className="text-neutral-200">{a.answer}</span></p>
          <p className="text-sm flex gap-2">{a.correct ? <Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" /> : <X className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />}<span>{a.feedback}</span></p>
          <details className="text-sm">
            <summary className="cursor-pointer text-sky-400" data-testid={`practice-solution-${i}`}>Full solution</summary>
            <div className="mt-2"><p className="mb-1"><b>Answer:</b> {q.answer}</p><Markdown>{q.explanation}</Markdown></div>
          </details>
        </div>
      )}
    </div>
  );
}

function PracticeContent() {
  const [sugg, setSugg] = useState([]);
  const [sets, setSets] = useState([]);
  const [topic, setTopic] = useState("");
  const location = useLocation();
  const [current, setCurrent] = useState(location.state?.set || null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const exam = params.get("mode") === "exam";
  useEffect(() => {
    const id = params.get("set");
    if (!id) return;
    http.get(`/practice/${id}`).then((r) => setCurrent(r.data)).catch((e) => toast.error(formatError(e)));
    setParams(exam ? { mode: "exam" } : {}, { replace: true });
  }, [params, setParams, exam]);

  const loadSets = useCallback(() => http.get("/practice").then((r) => setSets(r.data)), []);
  useEffect(() => { http.get("/practice/suggestions").then((r) => setSugg(r.data)); loadSets(); }, [loadSets]);
  useEffect(() => { if (location.state?.set) window.history.replaceState({}, ""); }, [location.state]);

  const job = useAiJob();
  const generate = async (t) => {
    if (busy) { job.stop(); setBusy(false); return; }
    setBusy(true);
    try {
      const { data } = await job.run((o) => http.post("/practice", { topic: t ?? topic, exam }, o));
      setCurrent(data);
      loadSets();
    } catch (e) { if (!isStopped(e)) toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const onAnswered = (i, d) => setCurrent((c) => ({ ...c, questions: c.questions.map((q, j) => (j === i ? { ...q, attempt: d.attempt, answer: d.answer, explanation: d.explanation } : q)) }));
  const done = current?.questions.filter((q) => q.attempt).length || 0;
  const right = current?.questions.filter((q) => q.attempt?.correct).length || 0;

  return (
    <div data-testid="practice-content">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="practice-heading">{exam ? "Sit a mock exam" : "Practice your weak spots"}</h2>
        <p className="text-neutral-400 mt-2 max-w-xl text-sm sm:text-base">{exam ? "A 10-question paper of mixed difficulty covering the whole topic, marked with feedback and full solutions." : "I build questions from the topics you've struggled with and your past mistakes."}</p>

        <div className="mt-6 flex gap-2">
          <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Any topic, e.g. derivatives, photosynthesis… (or leave blank)" className="h-11 bg-white/5 border-white/10" data-testid="practice-topic-input" />
          <button onClick={() => generate()} data-testid="practice-generate-btn"
            className="px-5 rounded-xl bg-sky-400 text-white text-sm font-medium hover:bg-sky-500 disabled:opacity-50 flex items-center gap-2 shrink-0">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} {busy ? "Stop" : "Generate"}
          </button>
        </div>
        {sugg.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="practice-suggestions">
            {sugg.map((s, i) => (
              <button key={`${s.subject}-${s.topic}`} disabled={busy} onClick={() => generate(`${s.subject} — ${s.topic}`)} data-testid={`practice-suggestion-${i}`}
                className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-neutral-300 hover:bg-white/5 transition-colors">
                {s.count > 0 && <span className="text-yellow-200 mr-1">weak</span>}{s.topic}
              </button>
            ))}
          </div>
        )}

        {current && (
          <div className="mt-10 space-y-5" data-testid="practice-set">
            <div className="flex items-end justify-between">
              <div>
                <p className="text-xs text-neutral-500 font-mono">{current.subject} / {current.topic}</p>
                <h2 className="font-display text-2xl font-semibold">{current.title}</h2>
              </div>
              <p className="text-sm text-neutral-400" data-testid="practice-score">{right}/{done} correct · {current.questions.length - done} left</p>
            </div>
            {current.questions.map((q, i) => <Question key={q.qid || `${current.set_id}-${i}`} q={q} i={i} setId={current.set_id} onAnswered={onAnswered} />)}
            {done === current.questions.length && (
              <>
                {right / done >= 0.7
                  ? <Celebrate testid="practice-celebrate" title="Great work!" text={`You got ${right} of ${done} right on ${current.topic}.`} />
                  : <div className="soft-card px-5 py-4 flex items-center gap-4" data-testid="practice-encourage">
                      <Mascot pose="determined" className="h-12 w-12 shrink-0" decorative />
                      <p className="text-sm text-neutral-300">{right} of {done} right. Try a new set on this topic — each round makes it stick.</p>
                    </div>}
              <div className="flex gap-3">
                <button onClick={() => generate(`${current.subject} — ${current.topic}`)} className="text-sm px-4 py-2 rounded-full border border-white/15 hover:bg-white/5 flex items-center gap-2" data-testid="practice-again-btn"><RotateCcw className="h-3.5 w-3.5" /> New set on this topic</button>
                <button onClick={() => navigate("/app")} className="text-sm px-4 py-2 rounded-full text-sky-400 hover:bg-sky-500/10" data-testid="practice-view-progress-btn">See my progress</button>
              </div>
              </>
            )}
          </div>
        )}

        <FlashcardsPanel />

        {sets.length > 0 && (
          <div className="mt-14">
            <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-600 mb-3">Past sets</p>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="practice-history">
              {sets.map((s) => (
                <button key={s.set_id} onClick={() => http.get(`/practice/${s.set_id}`).then((r) => setCurrent(r.data))} data-testid={`practice-set-${s.set_id}`}
                  className="text-left p-4 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:-translate-y-0.5 transition-[transform,background-color]">
                  <p className="text-sm font-medium truncate">{s.title}</p>
                  <p className="text-xs text-neutral-500 mt-1">{s.correct}/{s.answered} correct · {s.total} questions</p>
                </button>
              ))}
            </div>
          </div>
        )}
    </div>
  );
}

export default function PracticePage() {
  return (
    <div className="flex-1 overflow-y-auto scroll-thin">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-8">
        <PracticeTabs />
        <Locked feature="practice" inline title="Practice sets and mock exams are on paid plans"
          text="Unlock them with a plan or a pass. Adding exams and their countdowns in My exams is free.">
          <PracticeContent />
        </Locked>
      </div>
    </div>
  );
}
